import React, {ReactNode} from 'react';
import classnames from 'classnames';
import {useField} from 'formik';
import {DatePicker as AntdDatepicker} from 'antd';
import dayjs from 'dayjs';
import 'dayjs/locale/he';
import ClearIcon from '../ClearIcon';
import locale from 'antd/es/date-picker/locale/he_IL';
import styles from './styles.module.scss';
import inputStyles from '../../styles/commonInput.module.scss';
import {toAbsoluteUrl} from '../../../../../_metronic/_helpers';

const SimpleSeparator = () => {
  const separator = ' - ';
  return <div className={'mx-n4'}>{separator}</div>;
};

interface DatePickerProps {
  className: string,
  label: ReactNode,
  value: Date | string | null,
  minDate?: Date | string,
  maxDate?: Date | string,
  onChange: (date: Date | null) => void,
  onClear?: () => void,
  placeholder: ReactNode,
  dateFormat?: string,
  clearable?: boolean,
  disabled?: boolean,
}

export const DatePicker = ({
                             className,
                             label,
                             value,
                             minDate,
                             maxDate,
                             onChange,
                             onClear,
                             placeholder,
                             dateFormat = 'DD-MM-YYYY',
                             clearable = false,
                             disabled = false,
                             ...rest
                           }: DatePickerProps) => {
  return (
    <AntdDatepicker
      value={value && dayjs(value)}
      className={classnames(
        inputStyles.input,
        className,
      )}
      placeholder={placeholder}
      onChange={date => onChange(date?.toDate() || null)}
      locale={locale}
      format={dateFormat}
      allowClear={clearable && <ClearIcon onClick={() => onClear && onClear()}/>}
      suffixIcon={<img src={toAbsoluteUrl('/media/common/Date_primary.svg')} alt="d" className={styles.suffixIcon}/>}
      showToday={false}
      disabled={disabled}
      minDate={minDate && dayjs(minDate)}
      maxDate={maxDate && dayjs(maxDate)}
      getPopupContainer={(triggerNode) => {
        return triggerNode.parentNode;
      }}
      {...(rest as any)}
    />
  );
};

interface FilterDatePickerProps extends DatePickerProps {
  customContainerStyles?: string;
}

export const FilterDatePicker = ({
                                   className,
                                   label,
                                   value,
                                   minDate,
                                   maxDate,
                                   onChange,
                                   onClear,
                                   placeholder,
                                   dateFormat = 'DD-MM-YYYY',
                                   clearable = false,
                                   disabled,
                                   customContainerStyles = 'position-relative',
                                   ...rest
                                 }: FilterDatePickerProps) => {
  return (
    <div className={customContainerStyles}>
      {label && (
        <div className={styles.filterLabel}>
          {label}
        </div>
      )}
      <AntdDatepicker
        value={value && dayjs(value)}
        className={classnames(
          inputStyles.input,
          className,
        )}
        placeholder={placeholder}
        onChange={date => onChange(date?.toDate() || null)}
        locale={locale}
        format={dateFormat}
        allowClear={clearable && <ClearIcon onClick={() => onClear && onClear()}/>}
        suffixIcon={<img
          src={toAbsoluteUrl('/media/common/Date_primary.svg')}
          alt="d" className={styles.filterSuffixIcon}/>
        }
        showToday={false}
        disabled={disabled}
        minDate={minDate && dayjs(minDate)}
        maxDate={maxDate && dayjs(maxDate)}
        getPopupContainer={(triggerNode) => {
          return triggerNode.parentNode;
        }}
        {...(rest as any)}
      />
    </div>
  );
};

interface FilterRangePickerProps {
  startDate?: string;
  endDate?: string;
  onChange: (fromDate: string, toDate: string) => void;
  dateFormat?: string;
  placeholder?: [string];
  clearable?: boolean;
  onClear?: () => void;
  minDate?: string | Date;
  maxDate?: string | Date;
  label?: string;
  className?: string;
  customContainerStyles?: string;
}

export const FilterRangePicker = ({
                                    className,
                                    onChange,
                                    dateFormat = 'DD-MM-YYYY',
                                    placeholder,
                                    startDate,
                                    endDate,
                                    clearable = false,
                                    onClear,
                                    minDate,
                                    maxDate,
                                    label,
                                    customContainerStyles = 'position-relative',
                                    ...rest
                                  }: FilterRangePickerProps) => {
  return (
    <div className={customContainerStyles}>
      {label && (
        <div className={styles.filterLabel}>
          {label}
        </div>
      )}
      <AntdDatepicker.RangePicker
        value={[
          startDate ? dayjs(startDate) : null,
          endDate ? dayjs(endDate) : null
        ]}
        className={classnames(inputStyles.input, className)}
        placeholder={placeholder}
        onChange={(dates) => {
          if (dates) {
            const [startDate, endDate] = dates;

            onChange(
              startDate ? startDate.format('YYYY-MM-DD') : '',
              endDate ? endDate.format('YYYY-MM-DD') : ''
            );
          } else {
            onChange('', '');
          }
        }}
        locale={locale}
        format={dateFormat}
        allowClear={clearable && <ClearIcon onClick={() => onClear && onClear()}/>}
        separator={<SimpleSeparator/>}
        suffixIcon={<img
          src={toAbsoluteUrl('/media/common/Date_primary.svg')}
          alt="d" className={styles.rangeFilterSuffixIcon}/>
        }
        showToday={false}
        getPopupContainer={(triggerNode) => {
          return triggerNode.parentNode;
        }}
        minDate={minDate && dayjs(minDate)}
        maxDate={maxDate && dayjs(maxDate)}
        {...(rest as any)}
      />
    </div>
  );
};

interface StyledFieldDatepickerProps extends DatePickerProps {
  customContainerStyles: string;
  customLabelStyles: string;
  customInputStyles?: string;
}

export const StyledFieldDatepicker = ({
                                        customContainerStyles,
                                        customLabelStyles,
                                        customInputStyles = '',
                                        label,
                                        value,
                                        onChange,
                                        onClear,
                                        placeholder,
                                        dateFormat = 'DD-MM-YYYY',
                                        clearable = false,
                                        disabled,
                                        minDate,
                                        maxDate,
                                        ...rest
                                      }: StyledFieldDatepickerProps) => {
  return (
    <div className={customContainerStyles}>
      {label &&
        <div className={classnames(styles.labelContainer, customLabelStyles)}>
          {label}
        </div>
      }
      <div>
        <AntdDatepicker
          value={value && dayjs(value)}
          className={classnames(
            inputStyles.input,
            customInputStyles,
          )}
          placeholder={placeholder}
          onChange={date => onChange(date?.toDate() || null)}
          locale={locale}
          format={dateFormat}
          allowClear={clearable && <ClearIcon onClick={() => onClear && onClear()}/>}
          suffixIcon={<img src={toAbsoluteUrl('/media/common/Date_primary.svg')} alt="d"
                           className={styles.suffixIcon}/>}
          showToday={false}
          disabled={disabled}
          getPopupContainer={(triggerNode) => {
            return triggerNode.parentNode;
          }}
          minDate={minDate && dayjs(minDate)}
          maxDate={maxDate && dayjs(maxDate)}
          {...(rest as any)}
        />
      </div>
    </div>
  );
};

interface FieldDatepickerProps extends StyledFieldDatepickerProps {
  fieldId: string;
}

export const FieldDatepicker = ({
                                  customContainerStyles,
                                  customLabelStyles,
                                  customInputStyles = '',
                                  label,
                                  fieldId,
                                  onChange,
                                  onClear,
                                  placeholder,
                                  dateFormat = 'DD-MM-YYYY',
                                  clearable = false,
                                  disabled,
                                  minDate,
                                  maxDate,
                                  ...rest
                                }: FieldDatepickerProps) => {
  const [field] = useField(fieldId);

  return (
    <div className={customContainerStyles}>
      {label &&
        <div className={classnames(styles.labelContainer, customLabelStyles)}>
          {label}
        </div>
      }
      <div>
        <AntdDatepicker
          value={field.value && dayjs(field.value)}
          placeholder={placeholder}
          onChange={date => onChange(date?.toDate() || null)}
          locale={locale}
          format={dateFormat}
          allowClear={clearable && <ClearIcon onClick={() => onClear && onClear()}/>}
          suffixIcon={<img src={toAbsoluteUrl('/media/common/Date_primary.svg')} alt="d"
                           className={styles.suffixIcon}/>}
          showToday={false}
          disabled={disabled}
          getPopupContainer={(triggerNode) => {
            return triggerNode.parentNode;
          }}
          minDate={minDate && dayjs(minDate)}
          maxDate={maxDate && dayjs(maxDate)}
          className={classnames(
            inputStyles.input,
            customInputStyles,
          )}
          {...(rest as any)}
        />
      </div>
    </div>
  );
};
